// Team Arlo on Today: his five paws for the day, the team streak and who's
// been carrying what — a shared game, not a contest.

import { Flame, PawPrint, Trophy, Users } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FIGURES } from '../../content/sources.ts';
import { useEvents, useHousehold, useLiveStatus, useProfile } from '../../data/hooks.ts';
import { firsts, upcomingFirsts, validArrival } from '../../domain/firsts.ts';
import { arloDay, didList, fairShare, teamStreak, weekFrom, type ArloDay, type DayRules, type PawId } from '../../domain/team.ts';
import { dayStart, formatDate, today } from '../../domain/time.ts';
import { SESSIONS_PER_DAY } from '../../domain/trainingplan.ts';
import { navigate } from '../../app/router.ts';
import { Avatar } from '../../ui/bits.tsx';
import { Card, Sheet } from '../../ui/primitives.tsx';
import { SourceList } from '../../ui/Sources.tsx';
import { toast } from '../../ui/toast.tsx';

/** What each paw does for him, tied to the goals. */
const WHY: Record<PawId, { goal: string; text: string }> = {
  outside: { goal: 'Well-behaved', text: 'Every wee outside, rewarded on the spot, is house training happening.' },
  fed: { goal: 'Comfortable', text: 'Meals on time keep his tummy settled and his toilet times predictable.' },
  rested: { goal: 'Calm', text: 'Puppies need 15–20 hours’ sleep. Most biting and zoomies are tiredness.' },
  learned: { goal: 'Well-behaved · No barking', text: 'Three five-minute sessions a day: little and often is how puppies learn.' },
  explored: { goal: 'Sociable', text: 'A calm new thing every day while his socialisation window is open.' },
};

function useTeam() {
  const events = useEvents();
  const household = useHousehold();
  const s = useLiveStatus(60_000);
  const rules: DayRules = useMemo(() => ({ mealTimes: s.meals.times, sessions: SESSIONS_PER_DAY }), [s.meals.times]);
  return useMemo(() => {
    const day = arloDay(events, s.now, s.now, rules);
    const week = Array.from({ length: 7 }, (_, i) => arloDay(events, dayStart(s.now, i - 6), s.now, rules));
    return {
      day,
      week,
      streak: teamStreak(events, s.now, rules),
      share: fairShare(events, household.people, household, weekFrom(s.now), s.now),
      now: s.now,
      ready: events.length > 0,
    };
  }, [events, household, rules, s.now]);
}

function Paws({ day, big }: { day: ArloDay; big?: boolean }) {
  return (
    <div className={`paws${big ? ' paws-big' : ''}`} role="img" aria-label={`${day.filled} of 5 paws: ${day.paws.map((p) => `${p.label} ${p.done ? 'done' : 'to do'}`).join(', ')}`}>
      {day.paws.map((p) => (
        <span key={p.id} className={`paw paw-${p.id}${p.done ? ' done' : ''}`}>
          <PawPrint aria-hidden />
          <small>{p.label}</small>
        </span>
      ))}
    </div>
  );
}

/**
 * Cheer progress made while the app is open: a paw filling, a five-paw day,
 * a streak milestone. What was already true when the page loaded (or was
 * cheered before a reload) stays quiet.
 */
function useCheers(day: ArloDay, streak: number, ready: boolean) {
  const base = useRef<{ filled: number; streak: number } | undefined>(undefined);
  useEffect(() => {
    if (!ready) return;
    const key = `arlo-cheer-${today()}`;
    const save = (v: { filled: number; streak: number }) => {
      try {
        localStorage.setItem(key, JSON.stringify(v));
      } catch {
        /* private mode */
      }
    };
    if (!base.current) {
      let stored: { filled: number; streak: number } | undefined;
      try {
        stored = JSON.parse(localStorage.getItem(key) ?? 'null') ?? undefined;
      } catch {
        stored = undefined;
      }
      base.current = { filled: Math.max(day.filled, stored?.filled ?? 0), streak: Math.max(streak, stored?.streak ?? 0) };
      return save(base.current);
    }
    const seen = base.current;
    if (day.perfect && seen.filled < 5) toast({ message: 'A five-paw day for Arlo!', detail: 'Toilet, food, sleep, learning and something new — every need met. Lovely teamwork.', icon: Trophy, tone: 'good', duration: 9000 });
    else if (day.filled > seen.filled) {
      const latest = day.paws.filter((p) => p.done).at(-1);
      toast({ message: `${day.filled} of 5 paws`, detail: latest ? `${latest.label}: ${latest.progress}.` : undefined, icon: PawPrint, tone: 'good' });
    }
    if (streak > seen.streak && [3, 7, 14, 21, 30].includes(streak)) toast({ message: `${streak} good days in a row`, detail: 'Team streak — keep it going together.', icon: Flame, tone: 'good', duration: 9000 });
    base.current = { filled: Math.max(day.filled, seen.filled), streak: Math.max(streak, seen.streak) };
    save(base.current);
  }, [day.filled, day.perfect, streak, ready]); // eslint-disable-line react-hooks/exhaustive-deps
}

/** Today's side column: the paws, the streak and the week's share. */
export function TeamCard() {
  const t = useTeam();
  const [open, setOpen] = useState(false);
  useCheers(t.day, t.streak.days, t.ready);
  const two = t.share.shares.length >= 2 && t.share.total > 0;
  return (
    <>
      <Card className="team-card" onClick={() => setOpen(true)}>
        <header className="team-head">
          <span className="eyebrow">
            <PawPrint aria-hidden className="inline-icon" /> Arlo’s day
          </span>
          <span className="team-score">{t.day.perfect ? 'Five-paw day!' : `${t.day.filled} of 5`}</span>
        </header>
        <Paws day={t.day} />
        <div className="team-foot small">
          {t.streak.days > 0 && (
            <span className="team-streak">
              <Flame aria-hidden className="inline-icon" /> {t.streak.days}-day streak
            </span>
          )}
          {/* Not a scoreboard: just the fond titles. */}
          {two && t.share.shares.some((s) => s.title) && (
            <span className="team-share">
              {t.share.shares
                .filter((s) => s.title)
                .map((s) => `${s.person.name}: ${s.title}`)
                .join(' · ')}
            </span>
          )}
        </div>
      </Card>
      <Sheet open={open} onClose={() => setOpen(false)} title="Team Arlo">
        <TeamDetail t={t} />
      </Sheet>
    </>
  );
}

function TeamDetail({ t }: { t: ReturnType<typeof useTeam> }) {
  const profile = useProfile();
  const household = useHousehold();
  const events = useEvents();
  const next = useMemo(() => upcomingFirsts(firsts(events, profile.homeDate, household, t.now, validArrival(profile.homeDate, profile.arrivedAt)), 1)[0], [events, profile.homeDate, profile.arrivedAt, household, t.now]);
  return (
    <div className="team-detail">
      <p className="muted small">Five things he needs every day. You fill them together; four or five makes a good day, and good days in a row make your streak.</p>
      <Paws day={t.day} big />
      <ul className="paw-list">
        {t.day.paws.map((p) => (
          <li key={p.id} className={p.done ? 'done' : ''}>
            <PawPrint aria-hidden className={`paw-dot paw-${p.id}`} />
            <div>
              <strong>{p.label}</strong> <span className="muted small">· {p.progress}</span>
              {p.todo && <p className="small paw-todo">To fill it: {p.todo}</p>}
              <p className="muted small">
                {WHY[p.id].text} <span className="paw-goal">{WHY[p.id].goal}</span>
              </p>
            </div>
          </li>
        ))}
      </ul>

      <h3 className="team-h">This week</h3>
      <div className="week-paws">
        {t.week.map((d) => (
          <span key={d.date} className={`week-paw${d.good ? ' good' : ''}${d.perfect ? ' perfect' : ''}`} title={`${formatDate(d.date, 'weekday')}: ${d.filled} of 5`}>
            <small>{formatDate(d.date, 'weekday').slice(0, 2)}</small>
            <strong>{d.filled}</strong>
          </span>
        ))}
      </div>
      <p className="small">
        <Flame aria-hidden className="inline-icon" /> {t.streak.days ? `${t.streak.days} good day${t.streak.days === 1 ? '' : 's'} in a row` : 'Four paws today starts a streak'}
        {!t.streak.today && t.streak.days > 0 ? ' — today can keep it going' : ''}.
      </p>

      <h3 className="team-h">
        <Users aria-hidden className="inline-icon" /> Together this week
      </h3>
      {t.share.total > 0 ? (
        <>
          <ul className="share-list">
            {t.share.shares
              .filter((s) => s.points > 0)
              .map((s) => (
                <li key={s.person.id}>
                  <Avatar person={s.person} size={28} />
                  <span>
                    <strong>{s.person.name}</strong>
                    {s.title && <span className="share-title"> · {s.title}</span>}
                    <span className="muted small share-did">{didList(s.byKind)}</span>
                  </span>
                </li>
              ))}
          </ul>
          {t.share.note && <p className={`callout small tone-${t.share.note.tone}`}>{t.share.note.text}</p>}
          <p className="muted small">Not a contest — just who’s been doing what over the last seven days, so it’s easy to swap jobs.</p>
        </>
      ) : (
        <p className="muted small">Pick who you are at the top of the screen before logging, and this fills in.</p>
      )}

      {next && (
        <>
          <h3 className="team-h">
            <Trophy aria-hidden className="inline-icon" /> Next first
          </h3>
          <button type="button" className="link small" onClick={() => navigate('/diary')}>
            {next.title}: {next.hint}
          </button>
        </>
      )}
      <SourceList refs={[...FIGURES.sleepHours.sources, ...FIGURES.socialWindow.sources]} />
    </div>
  );
}
